import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Loader2, Wallet, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { connectWallet, isUserRejection } from "@/lib/walletSign";
import { ensureKrownNetwork } from "@/lib/cryptoSend";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { GetQastleCard } from "@/components/common/GetQastleCard";

const CHAIN_LABEL = {
  ethereum: "Ethereum", base: "Base", arbitrum: "Arbitrum", optimism: "Optimism", polygon: "Polygon",
  bsc: "BNB Chain", avalanche: "Avalanche", linea: "Linea", scroll: "Scroll", fantom: "Fantom",
  krown: "KROWN Network", solana: "Solana", bitcoin: "Bitcoin",
};
const short = (addr) => (addr && addr.length > 12 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr);

// Shown when joining a token-/NFT-gated BLOQ returns 402 gate_unmet. Runs the wallet-proof flow
// (connect → /wallet/challenge → sign → /gate/verify), then re-joins on success.
export function BloqGateDialog({ open, onOpenChange, bloq, onVerified }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("");
  const gate = bloq?.gate || {};
  const family = gate.family || "evm";

  const requirement = gate.display
    || (gate.type === "nft"
      ? `Hold an NFT from ${short(gate.contract)}`
      : gate.contract
        ? `Hold ${gate.min_balance || "1"}+ ${gate.symbol || "tokens"}`
        : `Hold ${gate.min_balance || "some"} ${gate.symbol || "native coin"}`);

  const verify = async (via) => {
    setBusy(true);
    try {
      setStep(via === "wc" ? "Opening WalletConnect…" : "Connecting wallet…");
      const signer = await connectWallet(family, via === "wc" ? { via: "walletconnect" } : {});
      if (gate.chain === "krown") {
        setStep("Switching to KROWN Network…");
        try { await ensureKrownNetwork(signer.provider); } catch { /* best-effort network nudge */ }
      }
      setStep("Awaiting signature…");
      const { nonce, message } = await api.walletChallenge(signer.family, { address: signer.address });
      const signature = await signer.sign(message);
      setStep("Verifying on-chain…");
      const res = await api.verifyBloqGate(bloq.id, { nonce, signature, wallet_address: signer.address });
      if (res?.gate_met) {
        toast.success("Wallet verified — joining…");
        onOpenChange(false);
        onVerified?.();
      } else {
        toast.error("This wallet doesn't meet the requirement.");
      }
    } catch (e) {
      const err = e?.response?.data?.detail;
      const code = typeof err === "object" ? err?.error : null;
      if (isUserRejection(e)) toast.error("Signature request canceled.");
      else if (e?.response?.status === 402 || code === "gate_unmet") toast.error("This wallet doesn't meet the requirement.");
      else if (e?.response?.status === 404) toast.info("Token-gated BLOQs aren't live yet.");
      else toast.error((typeof err === "object" ? err?.message : err) || e?.message || "Couldn't verify your wallet.");
    } finally {
      setBusy(false);
      setStep("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-bloq-s1 text-white sm:max-w-sm" data-testid="bloq-gate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <KeyRound className="h-5 w-5 text-bloq-purple" /> Token-gated BLOQ
          </DialogTitle>
          <DialogDescription className="text-sm text-bloq-muted">
            <span className="font-semibold text-white/85">{bloq?.name}</span> requires an on-chain asset to join.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl border border-bloq-purple/25 bg-bloq-purple/[0.06] p-4" data-testid="gate-requirement">
            <div className="text-xs font-semibold uppercase tracking-wider text-bloq-purple">Requirement</div>
            <div className="mt-1 text-sm font-medium text-white">{requirement}</div>
            {gate.chain && <div className="mt-1 text-xs text-bloq-muted">on {CHAIN_LABEL[gate.chain] || gate.chain}</div>}
          </div>
          <div className="flex items-start gap-2 rounded-xl border border-white/8 bg-bloq-s2 px-4 py-3 text-xs text-bloq-muted" data-testid="gate-wallet">
            <Wallet className="mt-0.5 h-4 w-4 shrink-0 text-bloq-mint" />
            <span>You'll sign a read-only message to prove ownership — no transaction, we never touch your keys.</span>
          </div>
          {busy && step && (
            <div className="flex items-center gap-2 text-xs font-semibold text-bloq-purple" data-testid="gate-step">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {step}
            </div>
          )}
          <Button onClick={() => verify()} disabled={busy} className="h-12 w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90" data-testid="gate-verify-btn">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><ShieldCheck className="mr-1.5 h-4 w-4" /> Verify wallet &amp; join</>}
          </Button>
          {family === "evm" && (
            <button
              onClick={() => verify("wc")}
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-full border border-bloq-mint/40 bg-bloq-mint/10 py-2.5 text-sm font-semibold text-bloq-mint transition-colors hover:bg-bloq-mint/15 disabled:opacity-50"
              data-testid="gate-verify-wc-btn"
            >
              <Wallet className="h-4 w-4" /> {gate.chain === "krown" ? "Connect Qastle Wallet · WalletConnect" : "Use WalletConnect · Qastle"}
            </button>
          )}
          {gate.chain === "krown" && <GetQastleCard compact testid="gate-get-qastle" />}
          <button onClick={() => { onOpenChange(false); navigate("/app/wallet"); }} className="w-full text-center text-xs text-bloq-muted hover:text-white" data-testid="gate-manage-wallets">
            Manage verified wallets
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
